import { compact, noop } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useToggle } from 'react-use';

import { FirstName, LastName } from '@dreryk/person-name-common-api';
import { GuardianPesel } from '@dreryk/personal-external-identifier-common-api';
import { PatientExternalIdentifier } from '@dreryk/egabinet-patient-published-language-api';

import { dateFormatter, useTranslate } from '@dreryk/react-common';
import {
   DataConfiguration,
   EmptyStatePlaceholder,
   rowIndexKeyExtractor,
   Table,
   TableColumnConfiguration,
   ValueRendererProps,
} from '@dreryk/react-components';

import { PersonalDataFormController } from '../common/common.types';
import { FormSectionHeader } from '../common/FormSectionHeader';
import { createGuardianExternalIdentifier } from '../common/GuardianModal/GuardianDetailsModal/GuardianDetailsModal';
import { GuardianModal } from '../common/GuardianModal/GuardianModal';
import { GuardianModalFormBean, StatutoryRepresentative } from '../common/GuardianModal/GuardianModal.types';
import { getDefaultPatientIdentifierType } from '../common/PersonForm/PatientIdentifierTypeField/PatientIdentifierTypeField';
import { GuardianCell } from '../PatientConfirmedUpdatePanel/PatientConfirmedUpdatePersonalData/sections/PatientGuardiansSection/GuardiansTable/cells/GuardianCell/GuardianCell';
import { PhoneNumberCell } from '../PatientConfirmedUpdatePanel/PatientConfirmedUpdatePersonalData/sections/PatientGuardiansSection/GuardiansTable/cells/PhoneNumberCell/PhoneNumberCell';
import { guardiansTranslationKeys } from '../translations';

import { ActionsCell } from './CreatePatientPersonalData/ActionsCell/ActionsCell';

const TABLE_TEXT_CELLS_WIDTH = '18%';

export const useGuardiansTableColumnConfiguration = (
   onEdit: VoidFunction,
   onDelete: (guardian: GuardianModalFormBean) => Promise<void> | void
): TableColumnConfiguration<GuardianModalFormBean>[] => {
   const translate = useTranslate();
   return [
      {
         name: 'guardian',
         accessor: noop,

         renderer: ({ bean }: ValueRendererProps<GuardianModalFormBean, any>) => (
            <GuardianCell
               firstName={FirstName.of(bean.guardian!.firstName!)}
               lastName={LastName.of(bean.guardian!.lastName!)}
               guardianExternalIdentifier={createGuardianExternalIdentifier(bean.guardian!)}
            />
         ),
         label: guardiansTranslationKeys.guardian,
         width: TABLE_TEXT_CELLS_WIDTH,
         sortingDisabled: true,
      },
      {
         name: 'phoneNumber',
         accessor: noop,

         renderer: ({ bean }: ValueRendererProps<GuardianModalFormBean, any>) => (
            <PhoneNumberCell phoneNumber={bean.guardian!.phoneNumber} />
         ),
         label: guardiansTranslationKeys.phone,
         width: TABLE_TEXT_CELLS_WIDTH,
         sortingDisabled: true,
      },
      {
         name: 'relation',
         accessor: 'relationCode',

         renderer: ({ bean }: ValueRendererProps<GuardianModalFormBean, any>) => (
            <>{translate(guardiansTranslationKeys.guardianRelation[bean.relationCode])}</>
         ),
         label: guardiansTranslationKeys.relation,
         width: TABLE_TEXT_CELLS_WIDTH,
         sortingDisabled: true,
      },
      {
         name: 'statutoryRepresentative',
         accessor: 'statutoryRepresentative',

         renderer: ({ bean }: ValueRendererProps<GuardianModalFormBean, any>) => (
            <>{translate(guardiansTranslationKeys[bean.statutoryRepresentative ? 'yes' : 'no'])}</>
         ),
         label: guardiansTranslationKeys.statutoryRepresentative,
         width: TABLE_TEXT_CELLS_WIDTH,
         sortingDisabled: true,
      },
      {
         name: 'creationDate',
         accessor: noop,
         formatter: dateFormatter,
         label: guardiansTranslationKeys.creationDate,
         width: TABLE_TEXT_CELLS_WIDTH,
         sortingDisabled: true,
      },
      {
         name: 'actions',
         accessor: noop,

         renderer: ({ bean }: ValueRendererProps<GuardianModalFormBean, any>) => (
            <ActionsCell onEdit={onEdit} onDelete={onDelete} guardian={bean} />
         ),
         label: '',
         width: `calc(100% - (5 * ${TABLE_TEXT_CELLS_WIDTH}))`,
         sortingDisabled: true,
      },
   ];
};

type CreatePatientGuardianTableProps = Pick<PersonalDataFormController, 'setValue'> & {
   readonly identifyingGuardian: GuardianModalFormBean | undefined;
};
export const CreatePatientGuardianTable = (props: CreatePatientGuardianTableProps): JSX.Element => {
   const { identifyingGuardian, setValue } = props;
   const translate = useTranslate();
   const [guardianModalOpened, toggleGuardianModalOpened] = useToggle(false);
   const onGuardianEdit = (): void => {
      toggleGuardianModalOpened(true);
   };
   // eslint-disable-next-line @typescript-eslint/require-await
   const onGuardianDeleted = (): void => {
      props.setValue('personalIdentifierType', getDefaultPatientIdentifierType());
   };
   const columnsConfiguration = useGuardiansTableColumnConfiguration(onGuardianEdit, onGuardianDeleted);
   const dataConfiguration = useMemo((): DataConfiguration<GuardianModalFormBean> => {
      return {
         data: compact([identifyingGuardian]),
      };
   }, [identifyingGuardian]);

   const handleGuardianModalSubmit = useCallback(
      (data: GuardianModalFormBean): void => {
         const guardianExternalIdentifier = createGuardianExternalIdentifier(data.guardian!);
         setValue('identifyingGuardian', data);
         setValue(
            'personalExternalIdentifier',
            guardianExternalIdentifier.accept<PatientExternalIdentifier | null>({
               visitPesel: (pesel) => PatientExternalIdentifier.of(GuardianPesel.of(pesel.asString())),
               visitPassport: () => null,
               visitNationalIdentificationNumber: () => null,
            })
         );
         toggleGuardianModalOpened(false);
      },
      [toggleGuardianModalOpened, setValue]
   );

   return (
      <div>
         <FormSectionHeader>{translate(guardiansTranslationKeys.guardians)}</FormSectionHeader>
         <Table
            testId={'guardians'}
            columns={columnsConfiguration}
            dataConfiguration={dataConfiguration}
            keyExtractor={rowIndexKeyExtractor}
            placeholder={
               <EmptyStatePlaceholder
                  heading={guardiansTranslationKeys.noGuardians}
                  subheading={guardiansTranslationKeys.noGuardiansAssignedToPatient}
                  testId={'noGuardians'}
               />
            }
         />
         {guardianModalOpened ? (
            <GuardianModal
               patientPesel={null}
               initialData={identifyingGuardian}
               onClose={() => {
                  toggleGuardianModalOpened(false);
               }}
               statutoryRepresentative={identifyingGuardian!.statutoryRepresentative === StatutoryRepresentative.YES}
               onSubmit={handleGuardianModalSubmit}
               isIdentifyingGuardian
            />
         ) : null}
      </div>
   );
};
